import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, Star, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cmsApi, apiError, fmtPrice } from "../api";
import { ImageUpload } from "../components/ImageUpload";
import { usePageData, PageTitle, Card, TextField, SelectField, Toggle, ConfirmDialog, OrderButtons, moveItem, Spinner, num } from "../components/admin-ui";

const EMPTY_SVC = { name: "", category_id: null, description: "", price: "", price_prefix: "", duration_minutes: "", image_media_id: null, featured: false, active: true };
const EMPTY_CAT = { name: "", description: "", active: true };

function ServiceDialog({ open, onClose, initial, categories, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
    setSaving(true);
    try {
      const body = { ...f, price: num(f.price), duration_minutes: num(f.duration_minutes) };
      delete body.id; delete body.order; delete body.created_at; delete body.updated_at; delete body.updated_by;
      if (f.id) await cmsApi.put(`/website-admin/services/${f.id}`, body); else await cmsApi.post("/website-admin/services", body);
      toast.success(f.id ? "Dienstleistung aktualisiert" : "Dienstleistung angelegt");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Dienstleistung bearbeiten" : "Neue Dienstleistung"}</DialogTitle><DialogDescription>Diese Angaben erscheinen öffentlich auf der Website.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-12 gap-4">
          <div className="sm:col-span-7 space-y-3">
            <TextField label="Name" value={f.name} onChange={(v) => set("name", v)} testid="service-name-input" />
            <SelectField label="Kategorie" value={f.category_id} onChange={(v) => set("category_id", v)} options={categories.map((c) => ({ value: c.id, label: c.name }))} testid="service-category-select" />
            <TextField label="Beschreibung" value={f.description} onChange={(v) => set("description", v)} textarea rows={3} testid="service-description-input" />
            <div className="grid grid-cols-3 gap-3">
              <TextField label="Präfix" value={f.price_prefix} onChange={(v) => set("price_prefix", v)} placeholder="ab" testid="service-prefix-input" />
              <TextField label="Preis (€)" type="number" step="0.5" min="0" value={f.price} onChange={(v) => set("price", v)} placeholder="leer = auf Anfrage" testid="service-price-input" />
              <TextField label="Dauer (Min.)" type="number" min="0" value={f.duration_minutes} onChange={(v) => set("duration_minutes", v)} testid="service-duration-input" />
            </div>
          </div>
          <div className="sm:col-span-5 space-y-3">
            <ImageUpload value={f.image_media_id} onChange={(id) => set("image_media_id", id)} kind="service" aspect="aspect-[4/3]" testid="service-image" />
            <Toggle label="Aktiv (öffentlich sichtbar)" checked={f.active} onChange={(v) => set("active", v)} testid="service-active-toggle" />
            <Toggle label="Hervorgehoben" checked={f.featured} onChange={(v) => set("featured", v)} testid="service-featured-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="service-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="service-save">{saving ? "Speichern…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
    setSaving(true);
    try {
      const body = { name: f.name, description: f.description, active: f.active };
      if (f.id) await cmsApi.put(`/website-admin/service-categories/${f.id}`, body); else await cmsApi.post("/website-admin/service-categories", body);
      toast.success("Kategorie gespeichert"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{f.id ? "Kategorie bearbeiten" : "Neue Kategorie"}</DialogTitle><DialogDescription>Kategorien gruppieren Ihre Leistungen.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <TextField label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="category-name-input" />
          <TextField label="Beschreibung (optional)" value={f.description} onChange={(v) => setF({ ...f, description: v })} textarea rows={2} testid="category-description-input" />
          <Toggle label="Aktiv" checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid="category-active-toggle" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="category-save">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServicesAdmin() {
  const { data, loading, reload } = usePageData("/website-admin/services");
  const [svcDialog, setSvcDialog] = useState(null);
  const [catDialog, setCatDialog] = useState(null);
  const [del, setDel] = useState(null);
  if (loading || !data) return <Spinner />;
  const cats = data.categories;
  const groups = [...cats.map((c) => ({ ...c, items: data.services.filter((s) => s.category_id === c.id) })), { id: null, name: "Ohne Kategorie", items: data.services.filter((s) => !cats.some((c) => c.id === s.category_id)) }].filter((g) => g.id || g.items.length);

  const reorderServices = async (group, i, dir) => {
    const moved = moveItem(group.items, i, dir);
    const all = groups.flatMap((g) => (g.id === group.id ? moved : g.items));
    try { await cmsApi.put("/website-admin/services/reorder", { ids: all.map((s) => s.id) }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const reorderCats = async (i, dir) => {
    try { await cmsApi.put("/website-admin/service-categories/reorder", { ids: moveItem(cats, i, dir).map((c) => c.id) }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const confirmDelete = async () => {
    try {
      await cmsApi.delete(del.type === "service" ? `/website-admin/services/${del.id}` : `/website-admin/service-categories/${del.id}`);
      toast.success("Gelöscht"); setDel(null); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="services-admin-page">
      <PageTitle title="Dienstleistungen" text="Leistungen, Preise und Kategorien der öffentlichen Website."
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatDialog(EMPTY_CAT)} data-testid="add-category-button"><Plus className="h-4 w-4 mr-1" /> Kategorie</Button>
          <Button onClick={() => setSvcDialog(EMPTY_SVC)} className="bg-[#4A1D24] hover:bg-[#3a161c] text-white" data-testid="add-service-button"><Plus className="h-4 w-4 mr-1" /> Dienstleistung</Button>
        </div>} />
      <div className="space-y-6">
        {groups.map((g, gi) => (
          <Card key={g.id || "none"} testid={`service-group-${g.id || "none"}`}>
            <div className="flex items-center gap-3 mb-4">
              {g.id && <OrderButtons testid={`cat-${g.id}`} disableUp={gi === 0} disableDown={gi === cats.length - 1} onUp={() => reorderCats(gi, -1)} onDown={() => reorderCats(gi, 1)} />}
              <div className="flex-1">
                <h2 className="font-serif text-xl text-stone-900 inline-flex items-center gap-2">{g.name}{g.id && !g.active && <EyeOff className="h-4 w-4 text-stone-400" />}</h2>
                {g.description && <p className="text-xs text-stone-500">{g.description}</p>}
              </div>
              {g.id && <>
                <button onClick={() => setCatDialog(g)} className="p-2 text-stone-400 hover:text-[#4A1D24]" data-testid={`cat-edit-${g.id}`} aria-label="Kategorie bearbeiten"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setDel({ type: "category", id: g.id, name: g.name })} className="p-2 text-stone-400 hover:text-red-700" data-testid={`cat-delete-${g.id}`} aria-label="Kategorie löschen"><Trash2 className="h-4 w-4" /></button>
              </>}
            </div>
            {g.items.length === 0 && <p className="text-sm text-stone-400 italic">Keine Leistungen in dieser Kategorie.</p>}
            <ul className="divide-y divide-stone-100">
              {g.items.map((s, i) => (
                <li key={s.id} className={`flex items-center gap-3 py-2.5 ${s.active ? "" : "opacity-50"}`} data-testid={`service-row-${s.id}`}>
                  <OrderButtons testid={`svc-${s.id}`} disableUp={i === 0} disableDown={i === g.items.length - 1} onUp={() => reorderServices(g, i, -1)} onDown={() => reorderServices(g, i, 1)} />
                  <div className="h-10 w-10 rounded-lg bg-[#F1EAE0] overflow-hidden shrink-0">{s.image_media_id && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/website/media/${s.image_media_id}`} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate inline-flex items-center gap-2">{s.name}{s.featured && <Star className="h-3.5 w-3.5 text-[#C59B6C] fill-[#C59B6C]" />}{!s.active && <span className="text-[10px] uppercase tracking-wide text-stone-400">inaktiv</span>}</p>
                    <p className="text-xs text-stone-500 inline-flex items-center gap-3">
                      <span>{fmtPrice(s.price) ? `${s.price_prefix ? s.price_prefix + " " : ""}${fmtPrice(s.price)}` : "auf Anfrage"}</span>
                      {s.duration_minutes ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_minutes} Min.</span> : null}
                    </p>
                  </div>
                  <button onClick={() => setSvcDialog({ ...EMPTY_SVC, ...s, price: s.price ?? "", duration_minutes: s.duration_minutes ?? "" })} className="p-2 text-stone-400 hover:text-[#4A1D24]" data-testid={`service-edit-${s.id}`} aria-label="Bearbeiten"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setDel({ type: "service", id: s.id, name: s.name })} className="p-2 text-stone-400 hover:text-red-700" data-testid={`service-delete-${s.id}`} aria-label="Löschen"><Trash2 className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
      {svcDialog && <ServiceDialog open initial={svcDialog} categories={cats} onClose={() => setSvcDialog(null)} onSaved={reload} />}
      {catDialog && <CategoryDialog open initial={catDialog} onClose={() => setCatDialog(null)} onSaved={reload} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={del?.type === "service" ? "Dienstleistung löschen?" : "Kategorie löschen?"} text={`„${del?.name}“ wird dauerhaft von der Website entfernt.${del?.type === "category" ? " Zugehörige Leistungen bleiben erhalten (ohne Kategorie)." : ""}`} onConfirm={confirmDelete} testid="delete-dialog" />
    </div>
  );
}
